import { useState, useMemo } from "react";
import cx from "classnames";
import { Box, Button, Flex, Text, Table, Tabs, Avatar } from "@radix-ui/themes";
import * as Popover from "@radix-ui/react-popover";
import * as Tooltip from "@radix-ui/react-tooltip";
import {
  MagnifyingGlassIcon,
  PlusIcon,
  Cross2Icon,
  PersonIcon,
  GroupIcon,
} from "@radix-ui/react-icons";
import { radixThemeDataAttributes } from "@/components/Theme";
import Select, { SelectOption } from "@/components/shared/select";
import styles from "./Agents.module.scss";
import React from "react";
import { TeamDetails } from "./TeamDetails";
import { AgentDetails } from "./AgentDetails";
import CreateTeamDialog from "./CreateTeamDialog";
import { useTeamsWithAgents, useAgents } from "../../api/useTeams";

interface Agent {
  id: string;
  name: string;
  teams: string[];
  externalId: string;
}

interface Team {
  id: string;
  name: string;
}

interface TeamMember {
  id: string;
  name: string;
}

interface TeamData {
  id?: string;
  name: string;
  agents?: TeamMember[];
}

interface TeamsData {
  [key: string]: TeamData;
}

interface TeamWithAgents {
  id: string;
  name: string;
  agents: TeamMember[];
}

export interface TeamMemberPopoverProps {
  team: Team;
  onAddMember: (agentId: string) => void;
  children?: React.ReactNode;
}

export function TeamMemberPopover({
  team,
  onAddMember,
  children,
}: TeamMemberPopoverProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const { data: teamsData, isLoading } = useTeamsWithAgents();
  const { data: agentsData = [] } = useAgents();

  const availableAgents = useMemo(() => {
    if (!teamsData || !agentsData) return [];

    const teamData = teamsData[team.name];
    const teamAgentIds = new Set(
      teamData?.agents?.map((agent) => agent.id) || []
    );

    return agentsData.filter(
      (agent) =>
        !teamAgentIds.has(agent.id) &&
        !teamAgentIds.has(agent.externalId) &&
        agent.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [team.name, searchQuery, teamsData, agentsData]);

  const handleAddAgent = async (agent: Agent) => {
    try {
      await onAddMember(agent.id);
      setIsOpen(false);
    } catch (error) {
      console.error("Failed to add agent:", error);
    }
  };

  return (
    <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
      <Popover.Trigger asChild>
        {children || (
          <Button variant="soft" color="gray" size="1">
            <PlusIcon />
            Add Agent
          </Button>
        )}
      </Popover.Trigger>
      <Popover.Content>
        <Box p="3">
          <SearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search agents..."
          />
          <Box mt="3">
            {isLoading ? (
              <Text size="2" color="gray" align="center">
                Loading agents...
              </Text>
            ) : availableAgents.length === 0 ? (
              <EmptyState
                icon={MagnifyingGlassIcon}
                title="No agents found"
                description={
                  searchQuery
                    ? `No agents match "${searchQuery}"`
                    : "No agents available to add"
                }
              />
            ) : (
              <Flex direction="column" gap="1">
                {availableAgents.map((agent) => (
                  <Button
                    key={agent.id}
                    variant="ghost"
                    color="gray"
                    onClick={() => handleAddAgent(agent)}
                  >
                    {agent.name}
                  </Button>
                ))}
              </Flex>
            )}
          </Box>
        </Box>
      </Popover.Content>
    </Popover.Root>
  );
}

const getInitials = (name: string) => {
  return name
    .split(" ")
    .map((word) => word[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

function EmptyState({
  icon: Icon,
  title,
  description,
  action,
}: {
  icon: React.ElementType;
  title: string;
  description: string;
  action?: React.ReactNode;
}) {
  return (
    <div className={styles.emptyState}>
      <Icon />
      <Text weight="medium" className={styles.title}>
        {title}
      </Text>
      <Text size="2" className={styles.description}>
        {description}
      </Text>
      {action && <div className={styles.action}>{action}</div>}
    </div>
  );
}

function SearchInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  return (
    <div className={styles.searchInput}>
      <MagnifyingGlassIcon />
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {value && (
        <button
          className={styles.resetButton}
          onClick={() => onChange("")}
          aria-label="Clear search"
        >
          <Cross2Icon />
        </button>
      )}
    </div>
  );
}

function transformTeamsData(data: TeamsData | undefined): TeamWithAgents[] {
  if (!data) return [];
  return Object.entries(data).map(([name, team]) => ({
    id: team.id || name,
    name,
    agents: team.agents || [],
  }));
}

export function Agents() {
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [teamSearchQuery, setTeamSearchQuery] = useState("");
  const [filterTeam, setFilterTeam] = useState("all");
  const {
    data: teamsData,
    isLoading: isTeamsLoading,
    refetch: refetchTeams,
  } = useTeamsWithAgents(true);
  const { data: agentsData = [], isLoading: isAgentsLoading } = useAgents(true);

  const teams = useMemo(() => {
    if (!teamsData) return [];
    return Object.entries(teamsData)
      .filter(([name]) => name.toLowerCase() !== "no team")
      .map(([name, team]) => ({
        id: team.id || name,
        name,
        agents: team.agents || [],
      }));
  }, [teamsData]);

  const transformedAgentsData = useMemo(() => {
    if (!agentsData || !teamsData) return [];

    return agentsData.map((agent): Agent => {
      const agentTeams: string[] = [];

      if (teamsData) {
        Object.entries(teamsData).forEach(([teamName, team]) => {
          if (!team.agents || teamName.toLowerCase() === "no team") return;

          const isMember = team.agents.some(
            (member) =>
              String(member.id) === String(agent.id) ||
              String(member.id) === String(agent.externalId)
          );

          if (isMember) {
            agentTeams.push(teamName);
          }
        });
      }

      return {
        ...agent,
        teams: agentTeams,
      };
    });
  }, [agentsData, teamsData]);

  const filteredAgents = useMemo(() => {
    if (!transformedAgentsData) return [];

    return transformedAgentsData.filter((agent) => {
      const matchesSearch = agent.name
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
      let matchesTeam = true;

      if (filterTeam === "unassigned") {
        matchesTeam = agent.teams.length === 0;
      } else if (filterTeam !== "all") {
        matchesTeam = agent.teams.includes(filterTeam);
      }

      return matchesSearch && matchesTeam;
    });
  }, [transformedAgentsData, searchQuery, filterTeam]);

  const filteredTeams = useMemo(() => {
    if (!teams) return [];

    return teams.filter(
      (team) =>
        team.name.toLowerCase() !== "no team" &&
        team.name.toLowerCase().includes(teamSearchQuery.toLowerCase())
    );
  }, [teams, teamSearchQuery]);

  const teamFilterOptions = useMemo<SelectOption[]>(() => {
    const options: SelectOption[] = [
      {
        label: "All Agents",
        value: "all",
      },
      {
        label: "Unassigned Agents",
        value: "unassigned",
      },
    ];

    return options.concat(
      teams.map((team) => ({
        label: team.name,
        value: team.name,
      }))
    );
  }, [teams]);

  if (isTeamsLoading || isAgentsLoading) {
    return (
      <Flex justify="center" py="8">
        <Text size="2" color="gray">
          Loading...
        </Text>
      </Flex>
    );
  }

  return (
    <>
      <Box>
        <Tabs.Root defaultValue="agents">
          <Tabs.List>
            <Tabs.Trigger value="agents">Agents</Tabs.Trigger>
            <Tabs.Trigger value="teams">Teams</Tabs.Trigger>
          </Tabs.List>

          <Box mt="6">
            <Tabs.Content value="agents">
              <Flex direction="column" gap="4" mb="6">
                <Flex direction="column" gap="1">
                  <Text size="6" weight="bold">
                    Agents
                  </Text>

                  <Text size="2" color="gray">
                    Manage your agents and their team assignments
                  </Text>
                </Flex>
                <Flex gap="3" align="center" justify="between">
                  <Box style={{ width: "300px" }}>
                    <SearchInput
                      value={searchQuery}
                      onChange={setSearchQuery}
                      placeholder="Search agents..."
                    />
                  </Box>

                  <Select
                    options={teamFilterOptions}
                    value={filterTeam}
                    onChange={setFilterTeam}
                  />
                </Flex>
              </Flex>
              <Table.Root variant="surface">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeaderCell>Agent</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Teams</Table.ColumnHeaderCell>
                  </Table.Row>
                </Table.Header>
                {filteredAgents.length === 0 ? (
                  <Table.Body>
                    <Table.Row>
                      <Table.Cell colSpan={2}>
                        <EmptyState
                          icon={searchQuery ? MagnifyingGlassIcon : PersonIcon}
                          title={
                            searchQuery ? "No agents found" : "No agents yet"
                          }
                          description={
                            searchQuery
                              ? `No agents match your search for "${searchQuery}". Try searching with a different term.`
                              : "Your agent list is empty. Agents added to your organization will appear here."
                          }
                        />
                      </Table.Cell>
                    </Table.Row>
                  </Table.Body>
                ) : (
                  <Table.Body>
                    {filteredAgents.map((agent) => (
                      <Table.Row
                        key={agent.id}
                        className={styles.agentRow}
                        onClick={() => setSelectedAgent(agent)}
                      >
                        <Table.Cell>
                          <Text weight="medium">{agent.name}</Text>
                        </Table.Cell>
                        <Table.Cell>
                          <Flex gap="2" wrap="wrap">
                            {agent.teams.length > 0 ? (
                              agent.teams.map((teamName) => (
                                <Box
                                  key={teamName}
                                  className={styles.teamBadge}
                                  data-team={teamName.toLowerCase()}
                                >
                                  <Text size="2">{teamName}</Text>
                                </Box>
                              ))
                            ) : (
                              <Text size="2" color="gray">
                                No team assigned
                              </Text>
                            )}
                          </Flex>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                )}
              </Table.Root>
            </Tabs.Content>

            <Tabs.Content value="teams">
              <Flex direction="column" gap="4" mb="6">
                <Flex justify="between" align="center">
                  <Box>
                    <Text size="6" weight="bold">
                      Teams
                    </Text>
                    <br />
                    <Text size="2" color="gray">
                      Organize your agents into teams for better management
                    </Text>
                  </Box>
                  <CreateTeamDialog refetch={refetchTeams} />
                </Flex>
                <Box style={{ width: "300px" }}>
                  <SearchInput
                    value={teamSearchQuery}
                    onChange={setTeamSearchQuery}
                    placeholder="Search teams..."
                  />
                </Box>
              </Flex>
              <Table.Root variant="surface">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeaderCell>Team</Table.ColumnHeaderCell>
                    <Table.ColumnHeaderCell>Agents</Table.ColumnHeaderCell>
                  </Table.Row>
                </Table.Header>
                {filteredTeams.length === 0 ? (
                  <Table.Body>
                    <Table.Row>
                      <Table.Cell colSpan={2}>
                        <EmptyState
                          icon={
                            teamSearchQuery ? MagnifyingGlassIcon : GroupIcon
                          }
                          title={
                            teamSearchQuery ? "No teams found" : "No teams yet"
                          }
                          description={
                            teamSearchQuery
                              ? `No teams match your search for "${teamSearchQuery}". Try searching with a different term.`
                              : "Create your first team to start organizing your agents."
                          }
                          action={
                            !teamSearchQuery && (
                              <CreateTeamDialog refetch={refetchTeams} />
                            )
                          }
                        />
                      </Table.Cell>
                    </Table.Row>
                  </Table.Body>
                ) : (
                  <Table.Body>
                    {filteredTeams.map((team) => (
                      <Table.Row
                        key={team.id}
                        className={styles.teamRow}
                        onClick={() => setSelectedTeam(team)}
                      >
                        <Table.Cell>
                          <Text weight="medium">{team.name}</Text>
                        </Table.Cell>
                        <Table.Cell>
                          {team.agents && team.agents.length > 0 ? (
                            <div className={styles.avatarGroup}>
                              {team.agents.slice(0, 5).map((agent) => (
                                <Tooltip.Provider
                                  key={agent.id}
                                  delayDuration={100}
                                >
                                  <Tooltip.Root>
                                    <Tooltip.Trigger asChild>
                                      <span className={styles.avatar}>
                                        <Avatar
                                          fallback={getInitials(agent.name)}
                                          size="2"
                                          radius="full"
                                        />
                                      </span>
                                    </Tooltip.Trigger>
                                    <Tooltip.Portal>
                                      <Tooltip.Content
                                        sideOffset={5}
                                        className={cx(
                                          styles.tooltipContent,
                                          "radix-themes"
                                        )}
                                        {...radixThemeDataAttributes}
                                      >
                                        {agent.name}
                                        <Tooltip.Arrow
                                          className={styles.tooltipArrow}
                                        />
                                      </Tooltip.Content>
                                    </Tooltip.Portal>
                                  </Tooltip.Root>
                                </Tooltip.Provider>
                              ))}
                              {team.agents.length > 5 && (
                                <Tooltip.Provider delayDuration={100}>
                                  <Tooltip.Root>
                                    <Tooltip.Trigger asChild>
                                      <span className={styles.avatar}>
                                        <Avatar
                                          fallback={`+${
                                            team.agents.length - 5
                                          }`}
                                          size="2"
                                          radius="full"
                                        />
                                      </span>
                                    </Tooltip.Trigger>
                                    <Tooltip.Portal>
                                      <Tooltip.Content
                                        sideOffset={5}
                                        className={cx(
                                          styles.tooltipContent,
                                          "radix-themes"
                                        )}
                                        {...radixThemeDataAttributes}
                                      >
                                        {team.agents.length - 5} more agents
                                        <Tooltip.Arrow
                                          className={styles.tooltipArrow}
                                        />
                                      </Tooltip.Content>
                                    </Tooltip.Portal>
                                  </Tooltip.Root>
                                </Tooltip.Provider>
                              )}
                            </div>
                          ) : (
                            <Text size="2" color="gray">
                              No agents in this team
                            </Text>
                          )}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                )}
              </Table.Root>
            </Tabs.Content>
          </Box>
        </Tabs.Root>
      </Box>

      {selectedAgent && (
        <AgentDetails
          agent={selectedAgent}
          onClose={() => setSelectedAgent(null)}
        />
      )}

      {selectedTeam && (
        <TeamDetails
          team={selectedTeam}
          onClose={() => setSelectedTeam(null)}
        />
      )}
    </>
  );
}
